import type { POI } from '../types';
import { pgSelect, pgInsert, pgUpdate, pgDelete, isSupabaseConfigured } from './supabaseClient';
import { loadConfig } from './configService';

/**
 * POI access layer — Supabase-backed (`pois` table). Supabase is the single
 * source of truth: if it's unconfigured or unreachable the picker shows an
 * empty list (with a console warning) rather than misleading sample data.
 *
 * Scoped by map_code (2026-07-21, user report: "I still see POIs when
 * selecting other maps in dashboard" — pois had NO map_code column at all
 * until this fix, so every map shared one global list). The current map
 * comes from loadConfig().code — the same "which map is this session on"
 * value the Dashboard's map switcher and the visitor AR session both already
 * use (see analyticsService.ts for the same internal-resolution pattern) —
 * rather than threading an explicit mapCode param through every one of
 * getPois()'s ~20 call sites across main.ts/AdminScreen.ts/ExploreLayer.ts/
 * MediaLayer.ts/MeshPreview.ts/etc.
 */

interface PoiRow {
  id: string;
  name: string;
  category: string;
  floor: number;
  x: number;
  y: number;
  z: number;
  node_id: string;
  description: string | null;
  icon: string | null;
  anchor_ref: string | null;
  media_type: 'image' | 'video' | 'model' | 'link' | 'audio' | null;
  media_url: string | null;
  media_start: string | null;
  media_end: string | null;
  capture: boolean | null;
  offer_text: string | null;
  offer_until: string | null;
  audience: 'all' | 'patient' | 'visitor' | 'staff' | null;
}

function rowToPoi(r: PoiRow): POI {
  return {
    id: r.id,
    name: r.name,
    category: r.category,
    floor: r.floor,
    position: { x: r.x, y: r.y, z: r.z },
    nodeId: r.node_id,
    description: r.description ?? undefined,
    icon: r.icon ?? undefined,
    anchorRef: r.anchor_ref ?? undefined,
    mediaType: r.media_type ?? undefined,
    mediaUrl: r.media_url ?? undefined,
    mediaStart: r.media_start ?? undefined,
    mediaEnd: r.media_end ?? undefined,
    capture: r.capture ?? undefined,
    offer: r.offer_text ?? undefined,
    offerUntil: r.offer_until ?? undefined,
    audience: r.audience ?? undefined,
  };
}

function poiToRow(p: POI): Record<string, unknown> {
  return {
    id: p.id,
    name: p.name,
    category: p.category,
    floor: p.floor,
    x: p.position.x,
    y: p.position.y,
    z: p.position.z,
    node_id: p.nodeId,
    description: p.description ?? null,
    icon: p.icon ?? null,
    anchor_ref: p.anchorRef ?? null,
    media_type: p.mediaType ?? null,
    media_url: p.mediaUrl ?? null,
    media_start: p.mediaStart ?? null,
    media_end: p.mediaEnd ?? null,
    capture: p.capture ?? false,
    offer_text: p.offer ?? null,
    offer_until: p.offerUntil ?? null,
    // pois.audience is NOT NULL default 'all' — an explicit null OVERRIDES
    // the default and violates the constraint (field-hit 2026-07-13:
    // "null value in column audience … violates not-null constraint" on
    // every Add POI since the industry-pack columns landed).
    audience: p.audience ?? 'all',
  };
}

let cache: POI[] | null = null;
let cacheMapCode: string | null = null;

const OFFLINE_KEY_PREFIX = 'navme.cache.pois.';
function offlineKey(mapCode: string): string {
  return `${OFFLINE_KEY_PREFIX}${mapCode || 'default'}`;
}

export async function getPois(forceRefresh = false): Promise<POI[]> {
  const mapCode = loadConfig().code;
  if (cache && !forceRefresh && cacheMapCode === mapCode) return cache;
  if (!isSupabaseConfigured()) {
    console.warn('getPois: database not configured — no POIs available');
    return [];
  }
  try {
    const rows = await pgSelect<PoiRow>('pois', `select=*&map_code=eq.${encodeURIComponent(mapCode)}&order=name`);
    cache = rows.map(rowToPoi);
    cacheMapCode = mapCode;
    // Offline snapshot: a backend blip mid-session shouldn't empty the picker.
    try {
      localStorage.setItem(offlineKey(mapCode), JSON.stringify(cache));
    } catch {
      /* quota */
    }
    return cache;
  } catch (err) {
    console.error('getPois: fetch failed — trying offline snapshot', err);
    try {
      const snap = JSON.parse(localStorage.getItem(offlineKey(mapCode)) ?? 'null') as POI[] | null;
      if (snap && snap.length > 0) return snap;
    } catch {
      /* corrupt snapshot */
    }
    return []; // not cached — retried on next call
  }
}

/**
 * STRICT variant for the Dashboard: throws on fetch failure instead of the
 * visitor-friendly silent []/offline-snapshot fallback in getPois(). An
 * empty result here means the table is REALLY empty — so the admin POI list
 * can tell "no POIs yet" apart from "couldn't load" (user-reported twice as
 * "edit/delete disappeared"). Refreshes the shared cache on success.
 */
export async function getPoisStrict(): Promise<POI[]> {
  if (!isSupabaseConfigured()) throw new Error('Database not configured — open ⚙️ Setup first.');
  const mapCode = loadConfig().code;
  const rows = await pgSelect<PoiRow>('pois', `select=*&map_code=eq.${encodeURIComponent(mapCode)}&order=name`);
  cache = rows.map(rowToPoi);
  cacheMapCode = mapCode;
  try {
    localStorage.setItem(offlineKey(mapCode), JSON.stringify(cache));
  } catch {
    /* quota */
  }
  return cache;
}

export function invalidatePoiCache(): void {
  cache = null;
  cacheMapCode = null;
}

export async function getPoiById(id: string): Promise<POI | undefined> {
  return (await getPois()).find((p) => p.id === id);
}

export async function searchPois(query: string): Promise<POI[]> {
  const q = query.trim().toLowerCase();
  const all = await getPois();
  if (!q) return all;
  return all.filter(
    (p) =>
      p.name.toLowerCase().includes(q) ||
      p.category.toLowerCase().includes(q) ||
      (p.description ?? '').toLowerCase().includes(q),
  );
}

/** Inserts a fully-formed POI (nodeId must already reference an existing nav_nodes row — see poiCreationService.ts for the usual entry point). */
export async function createPoi(poi: POI): Promise<POI> {
  const row = await pgInsert<PoiRow>('pois', { ...poiToRow(poi), map_code: loadConfig().code });
  invalidatePoiCache();
  return rowToPoi(row);
}

export async function updatePoi(
  id: string,
  // null explicitly clears media/offer/audience fields; undefined leaves them untouched.
  patch: Partial<Omit<POI, 'id' | 'mediaType' | 'mediaUrl' | 'mediaStart' | 'mediaEnd' | 'offer' | 'offerUntil' | 'audience'>> & {
    mediaType?: POI['mediaType'] | null;
    mediaUrl?: string | null;
    mediaStart?: string | null;
    mediaEnd?: string | null;
    offer?: string | null;
    offerUntil?: string | null;
    audience?: POI['audience'] | null;
  },
): Promise<POI> {
  const partialRow: Record<string, unknown> = {};
  if (patch.name !== undefined) partialRow.name = patch.name;
  if (patch.category !== undefined) partialRow.category = patch.category;
  if (patch.floor !== undefined) partialRow.floor = patch.floor;
  if (patch.position !== undefined) {
    partialRow.x = patch.position.x;
    partialRow.y = patch.position.y;
    partialRow.z = patch.position.z;
  }
  if (patch.nodeId !== undefined) partialRow.node_id = patch.nodeId;
  if (patch.description !== undefined) partialRow.description = patch.description ?? null;
  if (patch.icon !== undefined) partialRow.icon = patch.icon ?? null;
  if (patch.anchorRef !== undefined) partialRow.anchor_ref = patch.anchorRef ?? null;
  if (patch.mediaType !== undefined) partialRow.media_type = patch.mediaType ?? null;
  if (patch.mediaUrl !== undefined) partialRow.media_url = patch.mediaUrl ?? null;
  if (patch.mediaStart !== undefined) partialRow.media_start = patch.mediaStart ?? null;
  if (patch.mediaEnd !== undefined) partialRow.media_end = patch.mediaEnd ?? null;
  if (patch.capture !== undefined) partialRow.capture = patch.capture;
  if (patch.offer !== undefined) partialRow.offer_text = patch.offer ?? null;
  if (patch.offerUntil !== undefined) partialRow.offer_until = patch.offerUntil ?? null;
  // 'all' (not null) clears — pois.audience is NOT NULL default 'all'.
  if (patch.audience !== undefined) partialRow.audience = patch.audience ?? 'all';

  const row = await pgUpdate<PoiRow>('pois', id, partialRow);
  invalidatePoiCache();
  return rowToPoi(row);
}

export async function deletePoi(id: string): Promise<void> {
  await pgDelete('pois', id);
  invalidatePoiCache();
}
